import React, { createContext, useContext, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { pastOrders } from '../data/orders';
import { pills } from '../data/pills';
import { pregnancyPlans } from '../data/pregnancyPlans';
import { getPanel } from '../utils/biomarkers';
import { getRecommendedPillIds } from '../utils/pack';
import type { Biomarker, Goal, Order, OrderRecord, Pill, PregnancyStage, User } from '../types/platform';
interface PlatformValue {
  authenticated: boolean;
  authenticate: () => void;
  switchProfile: () => void;
  user: User | null;
  goal: Goal | null;
  stage: PregnancyStage;
  selectedPlanId: PregnancyStage;
  customPillIds: string[];
  order: Order | null;
  orders: OrderRecord[];
  panel: Biomarker[];
  packPills: Pill[];
  packPrice: number;
  signIn: (user: User) => void;
  signOut: () => void;
  chooseGoal: (goal: Goal, stage: PregnancyStage) => void;
  selectPlan: (id: PregnancyStage) => void;
  togglePill: (id: string) => void;
  setPackPills: (ids: string[]) => void;
  placeOrder: () => void;
}
const PlatformContext = createContext<PlatformValue | null>(null);
const addresses: Record<Goal, string> = {
  pregnancy: 'Villa 12, Al Barsha 2, Dubai',
  longevity: 'Apt 1804, Al Reem Island, Abu Dhabi'
};
export function PlatformProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [stage, setStage] = useState<PregnancyStage>('trimester-2');
  const [selectedPlanId, setSelectedPlanId] = useState<PregnancyStage>('trimester-2');
  const [customPillIds, setCustomPillIds] = useState<string[]>([]);
  const [order, setOrder] = useState<Order | null>(null);
  const [placedOrders, setPlacedOrders] = useState<OrderRecord[]>([]);
  const panel = useMemo(() => getPanel(goal ?? 'pregnancy'), [goal]);
  const value = useMemo<PlatformValue>(() => {
    const applyGoal = (g: Goal, s: PregnancyStage) => {
      setGoal(g);
      setStage(s);
      setSelectedPlanId(s);
      if (g === 'longevity') setCustomPillIds(getRecommendedPillIds(getPanel('longevity')));
      setOrder(null);
    };
    const plan = pregnancyPlans.find(p => p.id === selectedPlanId);
    const ids = goal === 'pregnancy' ? plan?.pillIds ?? [] : customPillIds;
    const packPills = ids.map(id => pills.find(p => p.id === id)).filter((p): p is Pill => Boolean(p));
    const packPrice = goal === 'pregnancy' ? plan?.price ?? 0 : packPills.reduce((sum, p) => sum + p.price, 0);
    const orders = [...placedOrders, ...pastOrders].filter(o => o.profileEmail === user?.email).sort((a, b) => b.placedAt.localeCompare(a.placedAt));
    return {
      authenticated,
      authenticate: () => setAuthenticated(true),
      switchProfile: () => {
        setUser(null);
        setGoal(null);
        setOrder(null);
      },
      user,
      goal,
      stage,
      selectedPlanId,
      customPillIds,
      order,
      orders,
      panel,
      packPills,
      packPrice,
      signIn: u => {
        setUser(u);
        applyGoal(u.goal, u.stage);
      },
      signOut: () => {
        setAuthenticated(false);
        setUser(null);
        setGoal(null);
        setOrder(null);
      },
      chooseGoal: (g, s) => {
        applyGoal(g, s);
        setUser(prev => prev ? {
          ...prev,
          goal: g,
          stage: s
        } : prev);
      },
      selectPlan: setSelectedPlanId,
      togglePill: id => setCustomPillIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]),
      setPackPills: setCustomPillIds,
      placeOrder: () => {
        if (!user || !goal) return;
        const now = new Date();
        const id = `NW-${Math.floor(100000 + Math.random() * 900000)}`;
        setOrder({
          id,
          placedAt: now
        });
        setPlacedOrders(prev => [{
          id,
          profileEmail: user.email,
          placedAt: format(now, "yyyy-MM-dd'T'HH:mm:ss"),
          label: goal === 'pregnancy' ? `${plan?.name ?? 'Pregnancy'} pack` : 'Wellbeing pack',
          pillIds: ids,
          total: packPrice,
          status: 'Processing',
          payment: goal === 'pregnancy' ? 'Visa •••• 4242' : 'Mastercard •••• 8810',
          address: addresses[goal]
        }, ...prev]);
      }
    };
  }, [authenticated, user, goal, stage, selectedPlanId, customPillIds, order, placedOrders, panel]);
  return <PlatformContext.Provider value={value} data-id="element-167" data-mp="3vwix4nwkj717">{children}</PlatformContext.Provider>;
}
export function usePlatform() {
  const ctx = useContext(PlatformContext);
  if (!ctx) throw new Error('usePlatform must be used within PlatformProvider');
  return ctx;
}